import React, { useRef, useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { useClickOutside } from "hooks/useClickOutside";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { useChallengesState } from "hooks/useChallengesState";
// import { CHALLENGE_STATUS } from 'pages/dashboard/challenges/state/constants';

const CHALLENGE_STATUS = {
  NEW: 'new',
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived'
};

const SelectChallenge = ({ category_id, instance_id }) => {
  const {
    getInstancesByCategory,
    getAttachedDraft,
    getCategory,
    getInstance,
    addChallengeDraft, 
    attachChallenge, 
    detachChallenge, 
    deleteDraft, 
    addChallengeInstance, 
    updateChallengeInstance,
    state
  } = useChallengesState();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [newDraftName, setNewDraftName] = useState("");
  const [instances, setInstances] = useState([]);
  const dropdownRef = useRef(null);
  
  useClickOutside(dropdownRef, () => setIsExpanded(false));

  const category = getCategory(category_id);
  const instance = getInstance(instance_id);
  const attachedDraft = getAttachedDraft(category_id, instance_id);

  // Update instances when category changes or instance updates
  useEffect(() => {
    // console.log('Effect triggered. Category:', category_id, 'Instance:', instance);
    const categoryInstances = getInstancesByCategory(category_id);
    // console.log('Category instances:', categoryInstances);
    setInstances(categoryInstances);
  }, [category_id, instance, getInstancesByCategory]);

  // Add a check for existing instance with same name
  const isInstanceNameExists = useMemo(() => {
    return instances.some(inst => 
      inst.name.toLowerCase().trim() === newDraftName.toLowerCase().trim()
    );
  }, [instances, newDraftName]);

  const handleAddNew = async () => {
    if (!newDraftName.trim()) return;

    // If this is a "New Challenge" instance, we'll update it directly
    if (instance?.name === 'New Challenge') {
      try {
        console.log('Updating instance. Current state:', { instance, newDraftName });
        const draftName = newDraftName.trim();
        setNewDraftName("");
        setIsExpanded(true);

        // Update instance name and status to draft
        const result = await updateChallengeInstance(instance.id, {
          ...instance,
          name: draftName,
          status: CHALLENGE_STATUS.DRAFT
        });

        console.log('Update result:', result);

        if (result?.error) {
          console.error('Error updating challenge name:', result.error);
          return;
        }
        
        setIsExpanded(true);
      } catch (err) {
        console.error('Unexpected error handling new challenge:', err);
      }
      return;
    }

    // Normal draft creation for existing challenges
    const newDraft = {
      name: newDraftName.trim(),
      category_id: category_id,
      status: CHALLENGE_STATUS.NEW,
      is_active: false,
      created_at: new Date().toISOString()
    };
    
    try {
      console.log('Creating new draft:', newDraft);
      const draftName = newDraftName.trim();
      setNewDraftName("");
      setIsExpanded(true);

      const result = await addChallengeDraft(category_id, newDraft);

      console.log('Add draft result:', result);
      
      if (result?.error) {
        console.error('Error creating draft:', result.error);
        return;
      }
    } catch (err) {
      console.error('Unexpected error creating draft:', err);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewDraftName(value);
    
    // Always expand dropdown when typing
    if (!isExpanded && value.trim()) {
      setIsExpanded(true);
    }
  };

  const handleInputFocus = (e) => {
    // If there's an attached draft, show it in the dropdown
    if (attachedDraft) {
      setIsExpanded(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && newDraftName.trim()) {
      e.preventDefault();
      handleAddNew();
    }
  };

  const handleDeleteDraft = async (e, draft) => {
    e.stopPropagation(); // Prevent triggering the draft click
    try {
      const { error } = await deleteDraft(category_id, draft.id);
      if (error) {
        console.error('Error deleting draft:', error);
        return;
      }
    } catch (err) {
      console.error('Unexpected error deleting draft:', err);
    }
  };

  const handleDraftClick = async (draft) => {
    if (attachedDraft?.id === draft.id) {
      // If clicking the same draft, just detach it
      try {
        const { error } = await detachChallenge(category_id, draft.id);
        if (error) {
          console.error('Error detaching challenge:', error);
          return;
        }
        setNewDraftName("");
      } catch (err) {
        console.error('Unexpected error detaching challenge:', err);
        return;
      }
    } else {
      // Check if draft is already attached to another instance
      const isAttachedToOther = Object.values(instance).some(
        inst => inst.id !== instance_id && inst.data?.draft_id === draft.id
      );

      if (isAttachedToOther) {
        console.warn('This draft is already attached to another instance:', draft.id);
        return;
      }

      // If there's a currently attached draft, detach it first
      if (attachedDraft?.id) {
        try {
          const { error } = await detachChallenge(category_id, attachedDraft.id);
          if (error) {
            console.error('Error detaching previous challenge:', error);
            return;
          }
        } catch (err) {
          console.error('Unexpected error detaching previous challenge:', err);
          return;
        }
      }

      // Now attach the new draft
      try {
        const { error } = await attachChallenge(category_id, {
          ...draft,
          id: instance_id
        });

        if (error) {
          console.error('Error attaching challenge:', error);
          return;
        }

        setNewDraftName("");
        setIsExpanded(false);
      } catch (err) {
        console.error('Unexpected error attaching challenge:', err);
      }
    }
  };

  // Add logging to render to see what's being displayed
  console.log('Render state:', { 
    instances, 
    category_id, 
    instance, 
    attachedDraft,
    newDraftName 
  });

  return (
    <ChallengeNameSection ref={dropdownRef}>
      <InputWrapper>
        <SelectedContent
          type="text"
          placeholder={category_id === 'uncategorized' ? "Select a category first..." : "Create new challenge..."}
          value={newDraftName}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          disabled={category_id === 'uncategorized'}
        />
        <ExpandArrow
          $isExpanded={isExpanded}
          $hasActiveChallenge={instances.length > 0}
          onClick={() => category_id !== 'uncategorized' && setIsExpanded(!isExpanded)}
          $isDisabled={category_id === 'uncategorized'}
          data-testid="expand-arrow"
        >
          <KeyboardArrowDownIcon />
        </ExpandArrow>
      </InputWrapper>

      <DropdownContent $isExpanded={isExpanded && category_id !== 'uncategorized'}>
        {/* Only show Add button if name doesn't exist and there's text */}
        {newDraftName.trim() && (
          <AddNewButton onClick={handleAddNew}>
            Add "{newDraftName}"
          </AddNewButton>
        )}

        {instances.length > 0 && (
          <>
            {instances.map(inst => {
              const isAttachedToOther = Object.values(instance).some(
                otherInst => otherInst.id !== instance_id && otherInst.data?.draft_id === inst.id
              );

              const isAttached = inst.id === attachedDraft?.id;
              const isMatchingInput = inst.name.toLowerCase().trim() === newDraftName.toLowerCase().trim();
              const isActive = inst.status === CHALLENGE_STATUS.ACTIVE;

              return (
                <DropdownItem
                  key={inst.id}
                  $isDisabled={isAttachedToOther || isActive}
                  $isActive={isAttached}
                  $isHighlighted={isMatchingInput}
                  onClick={() => !isAttachedToOther && !isActive && handleDraftClick(inst)}
                >
                  <ItemContent $hasName={!!inst.name}>
                    {inst.name === 'New Challenge' && newDraftName ? newDraftName : (inst.name || "Unnamed Challenge")}
                    {isActive && <ActiveLabel>(Active)</ActiveLabel>}
                  </ItemContent>
                  <ActionButtons>
                    {isActive ? (
                      <ActivateLink>
                        Detach
                      </ActivateLink>
                    ) : !isAttachedToOther && (
                      <DeleteButton
                        onClick={(e) => handleDeleteDraft(e, inst)}
                        title="Delete draft"
                      >
                        <CloseIcon />
                      </DeleteButton>
                    )}
                  </ActionButtons>
                </DropdownItem>
              );
            })}
          </>
        )}

        {instances.length === 0 && !newDraftName.trim() && (
          <EmptyState>
            No challenges yet
          </EmptyState>
        )}
      </DropdownContent>
    </ChallengeNameSection>
  );
};

const ChallengeNameSection = styled.div.attrs({
  className: 'ChallengeNameSection'
})`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 2;
  background: white;
  border-radius: 3px;
`;

const InputWrapper = styled.div.attrs({
  className: "InputWrapper",
})`
  display: flex;
  align-items: center;
  position: relative;
  background: #f1f1f1;
  border-radius: 3px;
  padding-right: 10px;
`;

const ExpandArrow = styled.div.attrs({
  className: "ExpandArrow",
})`
  color: ${props => props.$isDisabled ? '#999' : (props.$hasActiveChallenge ? '#0086d0' : '#666')};
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0)'};
  
  &:hover {
    color: ${props => props.$isDisabled ? '#999' : (props.$hasActiveChallenge ? '#017cbf' : '#333')};
  }
  
  svg {
    font-size: 21px;
  }
`;

const SelectedContent = styled.input.attrs({
  className: "SelectedContent",
})`
  flex: 1;
  padding: 0px;
  background: transparent;
  margin: 0px 10px;
  min-height: 30px;
  border: none;
  outline: none;
  font-size: 13px;
  padding-left: 0px;
  font-family: PT Sans;
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: transparent;
    color: #999;
  }
`;

const DropdownContent = styled.div.attrs({
  className: "DropdownContent",
})`
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  box-sizing: border-box;
  padding: 10px;
  background: white;
  gap: 2px;
  display: flex;
  flex-direction: column;
  opacity: ${(props) => (props.$isExpanded ? 1 : 0)};
  visibility: ${(props) => (props.$isExpanded ? "visible" : "hidden")};
  z-index: 5;
  border-radius: 0px 0px 3px 3px;
  overflow-y: auto;
  max-height: 300px;
  font-family: PT Sans;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: 300px;
`;

const DropdownItem = styled.div.attrs({
  className: 'DropdownItem'
})`
  border-radius: 2px;
  padding: 5px 8px;
  font-size: 13px;
  color: ${props => props.$isDisabled ? '#999' : (props.$isActive ? 'black' : 'black')};
  font-weight: ${props => props.$isHighlighted ? '600' : '400'};
  background: ${props => {
    if (props.$isActive) return '#e6f3ff';
    if (props.$isHighlighted) return '#f0f0f0';
    return 'white';
  }};
  border: none;
  outline: none;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  font-family: PT Sans;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: fit-content;
  min-width: 200px;
  opacity: ${props => props.$isDisabled ? 0.5 : 1};
  &:hover {
    background: ${props => !props.$isDisabled && (props.$isActive ? '#d9ecff' : '#f5f5f5')};
  }
`;

const ItemContent = styled.div.attrs({
  className: "ItemContent"
})`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: ${props => !props.$hasName ? 'italic' : 'normal'};
`;

const ActivateLink = styled.span.attrs({
  className: 'ActivateLink'
})`
  color: #0086d0;
  font-size: 12px;
  margin-left: 10px;
  cursor: pointer;
  font-weight: 700;
  text-decoration: underline;
`;

const EmptyState = styled.div.attrs({
  className: "EmptyState"
})`
  padding: 5px 10px;
  text-align: left;
  color: #666;
  font-size: 13px;
  border-radius: 3px;
  background: #f1f1f1;
  width: fit-content;
`;

const AddNewButton = styled.button.attrs({
  className: 'AddNewButton'
})`
  padding: 8px 10px;
  background: #f7f7f7;
  border: none;
  border-radius: 3px;
  color: #0086d0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  width: fit-content;
  text-align: left;
  margin-bottom: 5px;
  
  &:hover {
    background: #e5e5e5;
  }
`;

const ActionButtons = styled.div.attrs({
  className: 'ActionButtons'
})`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeleteButton = styled.button.attrs({
  className: 'DeleteButton'
})`
  background: none;
  border: none;
  padding: 2px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  border-radius: 3px;

  &:hover {
    opacity: 1;
    color: #ff4d4d;
    background: rgba(255, 77, 77, 0.1);
  }

  svg {
    font-size: 16px;
  }
`;

const ActiveLabel = styled.span.attrs({
  className: 'ActiveLabel'
})`
  margin-left: 8px;
  font-size: 11px;
  color: #666;
  font-style: italic;
`;

export default SelectChallenge;

